import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Mail, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Use XMLHttpRequest to avoid fetch interceptor issues
      const formData = new FormData();
      formData.append('email', email);

      const response = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BACKEND_URL}/api/auth/client/forgot-password`);
        
        xhr.onload = () => {
          resolve({
            ok: xhr.status >= 200 && xhr.status < 300,
            status: xhr.status,
            json: async () => JSON.parse(xhr.responseText)
          });
        };
        
        xhr.onerror = () => reject(new Error(t('forgot_network_error')));
        xhr.send(formData);
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle array of errors or single error
        let errorMessage = t('forgot_toast_error_send');
        if (data.detail) {
          if (Array.isArray(data.detail)) {
            errorMessage = data.detail.map(err => err.msg || err).join(', ');
          } else if (typeof data.detail === 'string') {
            errorMessage = data.detail;
          }
        }
        throw new Error(errorMessage);
      }

      // Show warning when email was not sent (backend returns email_sent: false on SMTP failure)
      if (data.email_sent !== true) {
        toast.warning(t('forgot_toast_email_not_sent'));
      } else {
        toast.success(t('forgot_toast_success'));
      }
      setTimeout(() => {
        navigate('/login');
      }, 1500);
      
    } catch (error) {
      console.error('Forgot password error:', error);
      const errorMessage = error.message || t('forgot_toast_error');
      toast.error(typeof errorMessage === 'string' ? errorMessage : t('forgot_toast_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{
      background: 'linear-gradient(135deg, #e8f9ed 0%, #d4f1e0 50%, #c8eed8 100%)'
    }}>
      <div className="w-full max-w-md mx-auto px-6">
        <Card className="glass-card" style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <CardHeader className="text-center py-6">
            <div className="flex items-center justify-center gap-3 mb-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_927b78ff-6137-4209-a5c6-1343491e5188/artifacts/04fvidd7_1c.png" 
                alt="AutoGo Icon" 
                className="h-12 w-12"
              />
              <span className="text-4xl font-bold" style={{color: '#0a3042'}}>AutoGo</span>
            </div>
            <CardTitle className="text-2xl font-bold" style={{color: '#0a3042'}}>
              {t('forgot_title')}
            </CardTitle>
            <CardDescription>
              {t('forgot_description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-gray-700 mb-2 block">
                  Correo Electrónico
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('forgot_email_placeholder')}
                    required
                    className="pl-10 py-6 text-base"
                  />
                </div>
              </div>

              <Button
                type="submit"
                size="lg"
                disabled={loading}
                className="w-full py-6 text-base font-semibold text-white"
                style={{backgroundColor: '#36e961'}}
              >
                {loading ? t('forgot_sending') : t('forgot_submit')}
              </Button>

              <div className="text-center mt-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="text-gray-600"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t('forgot_back_login')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;